import React, { useEffect, useRef, useState } from 'react';
import '../App.css';
import Wheel from '../components/Wheel';
import ConfettiBurst from '../components/ConfettiBurst';
import { API_BASE_URL } from '../config';

// --- Type Definitions (matching Go backend) ---
type SliceStyle = {
  backgroundColor: string;
  textColor: string;
};

type Slice = {
  id: number;
  label: string;
  value: string;
  probability: number;
  style: SliceStyle;
};

type WheelSettings = {
  slices: Slice[];
  wheelTitle: string;
  wheelSubtitle: string;
  buttonText: string;
  buttonBackground: string;
  buttonTextColor: string;
  backgroundColor: string;
  panelTextColor: string;
  markerColor: string;
  outerDotColor: string;
  centerImage: string;
  cooldownEnabled: boolean;
};

type SpinResponse = {
  message: string;
  prize: Slice;
  spinId: string;
  isMember: boolean;
  member?: {
    id?: string;
    name?: string;
    surname?: string;
    email?: string;
    phone?: string;
    username?: string;
  };
  coupon?: {
    code: string;
    rewardType: string;
    rewardValue?: number;
  };
};

const COOLDOWN_KEY = 'lastSpinTime';
const COOLDOWN_HOURS = 24;
const SESSION_STORAGE_KEY = 'wheelSessionId';

function WheelPage() {
  const [settings, setSettings] = useState<WheelSettings | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  
  const [spinning, setSpinning] = useState<boolean>(false);
  const [winningSliceIndex, setWinningSliceIndex] = useState<number | null>(null);
  const [prize, setPrize] = useState<Slice | null>(null);
  const [coupon, setCoupon] = useState<SpinResponse['coupon'] | null>(null);

  const [cooldownTime, setCooldownTime] = useState<number>(0);
  const [phone, setPhone] = useState<string>('');
  const [formError, setFormError] = useState<string | null>(null);
  const [showConfetti, setShowConfetti] = useState<boolean>(false);
  const confettiTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const sessionIdRef = useRef<string>('');

  // Check cooldown on mount and set interval to update timer
  useEffect(() => {
    const lastSpin = localStorage.getItem(COOLDOWN_KEY);
    if (lastSpin) {
      const remaining = (parseInt(lastSpin, 10) + (COOLDOWN_HOURS * 60 * 60 * 1000)) - Date.now();
      if (remaining > 0) {
        setCooldownTime(remaining);
      }
    }

    const interval = setInterval(() => {
      setCooldownTime(prev => (prev > 1000 ? prev - 1000 : 0));
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  // Fetch config from backend
  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/config`);
        if (!response.ok) throw new Error('Network response was not ok');
        const data: WheelSettings = await response.json();
        setSettings({
          ...data,
          wheelSubtitle: data.wheelSubtitle || 'İndirim Yakala',
          buttonBackground: data.buttonBackground || '#FFFFFF',
          buttonTextColor: data.buttonTextColor || '#0D0D0D',
          backgroundColor: data.backgroundColor || '#060606',
          panelTextColor: data.panelTextColor || '#FFFFFF',
          markerColor: data.markerColor || '#FF3B30',
          outerDotColor: data.outerDotColor || '#FFCC00',
          centerImage: data.centerImage || '',
          cooldownEnabled: typeof data.cooldownEnabled === 'boolean' ? data.cooldownEnabled : true,
        });
      } catch (e) {
        setError('Failed to load wheel configuration.');
      } finally {
        setLoading(false);
      }
    };
    fetchConfig();
  }, []);

  useEffect(() => {
    return () => {
      if (confettiTimeoutRef.current) {
        clearTimeout(confettiTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    let sessionId = window.sessionStorage.getItem(SESSION_STORAGE_KEY);
    if (!sessionId) {
      sessionId = `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
      window.sessionStorage.setItem(SESSION_STORAGE_KEY, sessionId);
    }
    sessionIdRef.current = sessionId;
  }, []);

  useEffect(() => {
    if (!settings) return;
    if (!settings.cooldownEnabled) {
      localStorage.removeItem(COOLDOWN_KEY);
      setCooldownTime(0);
    }
  }, [settings]);

  const handleSpin = async () => {
    if (spinning || !settings) return;
    if (settings.cooldownEnabled && cooldownTime > 0) return;
    if (!phone.trim()) {
      setFormError('Lütfen önce telefon numaranızı girin.');
      return;
    }
    if (!phone.trim().startsWith('05')) {
      setFormError('Telefon numarası 05 ile başlamalıdır.');
      return;
    }
    setFormError(null);
    setPrize(null);
    setCoupon(null);
    setWinningSliceIndex(null);

    try {
      const response = await fetch(`${API_BASE_URL}/spin`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          phoneNumber: phone.trim(),
          sessionId: sessionIdRef.current || undefined,
        }),
      });
      if (!response.ok) {
        const errorPayload = await response.json().catch(() => null);
        const message = errorPayload?.error || 'Spin request failed';
        throw new Error(message);
      }
      const result: SpinResponse = await response.json();
      const winningSlice = result.prize;

      const sliceIndex = settings.slices.findIndex(s => s.value === winningSlice.value);
      if (sliceIndex === -1) throw new Error("Winning slice not found");
      
      setPrize(winningSlice);
      setCoupon(result.coupon ?? null);
      setWinningSliceIndex(sliceIndex);
      setSpinning(true);
    } catch (e: any) {
      setFormError(e.message || 'Çark çevrilirken bir hata oluştu. Lütfen daha sonra tekrar deneyin.');
    }
  };

  const onSpinEnd = () => {
    setSpinning(false);
    if (settings?.cooldownEnabled) {
      const now = Date.now();
      localStorage.setItem(COOLDOWN_KEY, now.toString());
      setCooldownTime(COOLDOWN_HOURS * 60 * 60 * 1000);
    }
    setShowConfetti(true);
    if (confettiTimeoutRef.current) {
      clearTimeout(confettiTimeoutRef.current);
    }
    confettiTimeoutRef.current = setTimeout(() => setShowConfetti(false), 1600);
  }

  const formatCooldown = (ms: number) => {
    const totalSeconds = Math.floor(ms / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    return `${hours} sa ${minutes} dk ${seconds} sn`;
  };

  if (loading) return <div className="flex items-center justify-center h-screen">Loading...</div>;
  if (error) return <div className="flex items-center justify-center h-screen bg-red-100">{error}</div>;
  if (!settings) return null;

  const theme = {
    background: settings.backgroundColor || '#060606',
    text: settings.panelTextColor || '#FFFFFF',
    accent: settings.outerDotColor || '#FFCC00',
    marker: settings.markerColor || '#FF3B30',
    buttonBg: settings.buttonBackground || '#FFFFFF',
    buttonText: settings.buttonTextColor || '#0D0D0D',
  };

  const isCooldownActive = settings.cooldownEnabled;
  const isOnCooldown = isCooldownActive && cooldownTime > 0;
  const isButtonDisabled = spinning || isOnCooldown;
  const buttonLabel = spinning
    ? 'Çevriliyor...'
    : isOnCooldown
      ? `Tekrar Dene (${formatCooldown(cooldownTime)})`
      : settings.buttonText;

  return (
    <div
      className="min-h-screen w-full flex items-center justify-center px-4 py-10"
      style={{ backgroundColor: theme.background }}
    >
      <ConfettiBurst show={showConfetti} colors={[theme.accent, theme.marker, '#ffffff']} />

      <div className="w-full max-w-6xl grid gap-10 lg:grid-cols-2 items-center">
        <div className="flex justify-center">
          <div className="relative">
            <Wheel
              slices={settings.slices}
              isSpinning={spinning}
              winningSliceIndex={winningSliceIndex}
              onSpinEnd={onSpinEnd}
              markerColor={theme.marker}
              outerDotColor={theme.accent}
              centerImage={settings.centerImage}
            />
          </div>
        </div>

        <div
          className="rounded-[32px] border border-white/10 bg-white/5 p-8 backdrop-blur-xl shadow-2xl"
          style={{ color: theme.text }}
        >
          <p className="text-xs uppercase tracking-[0.7em]" style={{ color: theme.accent }}>
            Sadakat Programı
          </p>
          <h1 className="mt-4 text-4xl font-black">{settings.wheelTitle}</h1>
          <p className="mt-2 text-lg text-white/80">{settings.wheelSubtitle}</p>

          <div className="mt-8 space-y-4">
            <div>
              <label className="text-sm text-white/70">Telefon Numaranız</label>
              <input
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="Telefon numaranızı girin"
                className="mt-2 w-full rounded-2xl border border-white/10 bg-white/10 px-5 py-4 text-lg text-white placeholder-white/50 focus:border-white focus:outline-none focus:ring-2 focus:ring-white/30"
              />
            </div>

            <button
              onClick={handleSpin}
              disabled={isButtonDisabled || !phone.trim().startsWith('05')}
              style={{ backgroundColor: theme.buttonBg, color: theme.buttonText }}
              className={`w-full rounded-2xl py-4 text-lg font-semibold uppercase tracking-wider shadow-xl transition-all duration-300 ${
                isButtonDisabled || !phone.trim().startsWith('05') ? 'opacity-60 cursor-not-allowed' : 'hover:-translate-y-1'
              }`}
            >
              {buttonLabel}
            </button>

            {isOnCooldown && (
              <p className="text-sm text-white/70">
                Tekrar çevirmek için {formatCooldown(cooldownTime)} kaldı.
              </p>
            )}
            {!isCooldownActive && (
              <p className="text-sm text-emerald-300">
                Test modu aktif: 24 saatlik bekleme süresi kapalı.
              </p>
            )}

            {formError && (
              <p className="text-sm text-red-400">
                {formError}
              </p>
            )}

            {prize && !spinning && winningSliceIndex !== null && (
              <div className="rounded-2xl border border-white/15 bg-black/40 p-6 text-center shadow-inner">
                <p className="text-sm uppercase tracking-[0.4em]" style={{ color: theme.accent }}>
                  Tebrikler
                </p>
                <h2 className="mt-2 text-2xl font-bold">{prize.label}</h2>
                <p className="mt-1 text-white/70">Ödülünüz hazır. Kısa süre içinde sizinle iletişime geçeceğiz.</p>
                {coupon && (
                  <div className="mt-4">
                    <p className="text-sm text-white/60">Kupon Kodunuz</p>
                    <p className="mt-1 text-2xl font-mono tracking-widest text-emerald-300">
                      {coupon.code}
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>

        </div>
      </div>
    </div>
  );
}

export default WheelPage;
